import React from 'react';
import { select } from 'd3-selection';
import { axisBottom, axisLeft, axisRight, line, scaleLinear, scalePoint } from 'd3';

import { NumberFormatter, NumberWithCommas, StrokeDasharrayValue } from '../../../helpers/Helpers';

import { ISalesAnalysisChart } from '../../../interfaces/charts.interface';

interface Props {
  dataSource: ISalesAnalysisChart[];
  component: string;
  period: string;
}

interface State {
  dataSource: ISalesAnalysisChart[];
  chartData: any[];
}

export default class LineChart extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      dataSource: this.props.dataSource,
      chartData: this.getChartData(this.props.dataSource)
    };
  }

  componentDidMount(): void {
    this.draw();
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
    if (this.props.dataSource !== prevProps.dataSource) {
      this.setState({
        dataSource: this.props.dataSource,
        chartData: this.getChartData(this.props.dataSource)
      }, () => {
        select(`.${this.props.component}`).selectAll('*').remove();
        this.draw();
      });
    }
  }

  getChartData(dataSource) {
    const items = dataSource.reduce((acc: any[], group) => {
      const list = group.children.map((subgroup) => {
        return {
          ...subgroup,
          group: group.name,
          values: subgroup.values.map((value) => {
            return {
              ...value,
              measure: subgroup.name
            };
          })
        };
      });
      acc.push(list);
      return acc;
    }, []);

    return [].concat.apply([], items);
  }

  draw(): void {
    if (this.state.dataSource.length === 0) {
      return;
    }

    // Set the dimensions and margins of the graph
    const margin = { top: 20, right: 60, bottom: 120, left: 150 },
      width = 1000 - margin.left - margin.right,
      height = 500 - margin.top - margin.bottom;

    // Append the SVG object to the page
    const svg = select(`.${this.props.component}`)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // List of x-axis labels
    const xAxis: string[] = this.state.dataSource[0].children[0].values.map((d) => d.label);

    // Add X-axis
    const x: any = scalePoint()
      .domain(xAxis)
      .range([0, width]);

    svg.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(axisBottom(x))
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('transform', 'rotate(-45)');

    // Find the max and min range of the left y-axis
    const yLeftMax = this.state.chartData.reduce((acc, group) => {
      const max = group.values
        .filter(subgroup => !subgroup.measure.includes('%'))
        .reduce((init, subgroup) => Math.max(init, subgroup.value), 0);
      return Math.max(acc, max);
    }, 0);

    const yLeftMin = this.state.chartData.reduce((acc, group) => {
      const min = group.values
        .filter(subgroup => !subgroup.measure.includes('%'))
        .reduce((init, subgroup) => Math.min(init, subgroup.value), 0);
      return Math.min(acc, min);
    }, 0);

    // Find the max and min range of the right y-axis
    const yRightMax = this.state.chartData.reduce((acc, group) => {
      const max = group.values
        .filter(subgroup => subgroup.measure.includes('%'))
        .reduce((init, subgroup) => Math.max(init, subgroup.value), 0);
      return Math.max(acc, max);
    }, 0);

    const yRightMin = this.state.chartData.reduce((acc, group) => {
      const min = group.values
        .filter(subgroup => subgroup.measure.includes('%'))
        .reduce((init, subgroup) => Math.min(init, subgroup.value), 0);
      return Math.min(acc, min);
    }, 0);

    // Add Y-axis (left side)
    const yLeft = scaleLinear()
      .domain([yLeftMin, yLeftMax])
      .range([height, 0]);

    /*svg.append('g')
      .call(axisLeft(yLeft));*/

    // Add Y-axis (right side)
    const yRight = scaleLinear()
      .domain([yRightMin, yRightMax])
      .range([height, 0]);

    /*svg.append('g')
      .attr('transform', `translate(${width},0)`)
      .call(axisRight(yRight));*/

    // Add a darker line on Y-axis 0 value for the left axis
    const yZeroLeft = yLeft(0);
    svg.append('line')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', yZeroLeft)
      .attr('y2', yZeroLeft)
      .style('stroke', '#505D69');

    // Add the lines (you will have two line sets, one for each axis)
    const valueLineLeft = line()
      .x((d: any) => x(d.label))
      .y((d: any) => yLeft(d.value));  // Use yLeft scale for the left axis

    const valueLineRight = line()
      .x((d: any) => x(d.label))
      .y((d: any) => yRight(d.value));  // Use yRight scale for the right axis

    // Add Y-axis grid lines for the left Y-axis
    svg.append('g')
      .attr('class', 'grid')
      .call(axisLeft(yLeft)
        .tickSize(0)
        .tickFormat((d: any) => NumberFormatter(d))
        .tickSizeOuter(0));

    // Add Y-axis grid lines for the right Y-axis
    svg.append('g')
      .attr('class', 'grid')
      .attr('transform', `translate(${width},0)`)
      .call(axisRight(yRight)
        .tickSize(0)
        .tickFormat((d: any) => `${d}%`)
        .tickSizeOuter(0));

    // Add the lines to the chart (left Y-axis data)
    this.state.chartData
      .filter(d => !d.name.includes('%'))
      .forEach((d) => {
        svg.append('path')
          .datum(d.values)
          .attr('stroke', d.color)
          .attr('stroke-dasharray', StrokeDasharrayValue(d.group))
          .attr('stroke-width', 3)
          .style('fill', 'none')
          .attr('d', valueLineLeft);  // Draw the line with the left Y-axis scale
      });

    // For the right Y-axis, you'd need to map the right data set.
    // Assuming that you want to plot a second data set using the right Y-axis:
    /*const rightData = this.state.chartData
      .filter(d => d.name.includes('%'))
      .map((d) => ({
        ...d,
        values: d.values.map(v => ({
          ...v,
          value: v.value * (yRightMax / yLeftMax) // Adjust the scaling factor
        }))
      }));

    rightData.forEach((d) => {
      svg.append('path')
        .datum(d.values)
        .attr('stroke', d.color)
        .attr('stroke-dasharray', StrokeDasharrayValue(d.group))
        .attr('stroke-width', 3)
        .style('fill', 'none')
        .attr('d', valueLineRight);  // Draw the line with the right Y-axis scale
    });*/

    this.state.chartData
      .filter(d => d.name.includes('%'))
      .forEach((d) => {
        svg.append('path')
          .datum(d.values)
          .attr('stroke', d.color)
          .attr('stroke-dasharray', StrokeDasharrayValue(d.group))
          .attr('stroke-width', 3)
          .style('fill', 'none')
          .attr('d', valueLineRight);  // Draw the line with the left Y-axis scale
      });

    // Tooltip (can be used for both left and right axes)
    const tooltip = select(`.${this.props.component}`)
      .append('div')
      .style('opacity', 0)
      .attr('class', 'tooltip');

    const mouseover = () => {
      tooltip.style('opacity', 1);
    };

    const mousemove = (event, d) => {
      tooltip.html(d.measure + '<br/>' + 'Value: ' + NumberWithCommas(d.value))
        .style('left', `${event.layerX + 10}px`)
        .style('top', `${event.layerY}px`);
    };

    const mouseleave = () => {
      tooltip.style('opacity', 0);
    };

    // Add points (circles) on the lines
    this.state.chartData.forEach((d) => {
      if (!d.name.includes('%')) {
        svg.append('g')
          .selectAll('dot')
          .data(d.values)
          .join('circle')
          .attr('class', 'myCircle')
          .attr('cx', (d: any) => x(d.label))
          .attr('cy', (d: any) => yLeft(d.value))  // Use left Y-axis scale for points on the left Y-axis lines
          .attr('r', 5)
          .attr('fill', d.color)
          .on('mouseover', mouseover)
          .on('mousemove', mousemove)
          .on('mouseleave', mouseleave);
      } else {
        svg.append('g')
          .selectAll('dot')
          .data(d.values)
          .join('circle')
          .attr('class', 'myCircle')
          .attr('cx', (d: any) => x(d.label))
          .attr('cy', (d: any) => yRight(d.value))  // Use left Y-axis scale for points on the right Y-axis lines
          .attr('r', 5)
          .attr('fill', d.color)
          .on('mouseover', mouseover)
          .on('mousemove', mousemove)
          .on('mouseleave', mouseleave);
      }
    });

    // Add X-axis and Y-axis labels
    /*svg.append('text')
      .attr('class', 'x-axis-label')
      .attr('x', width / 2)
      .attr('y', height + 80)
      .text('Period');

    svg.append('text')
      .attr('class', 'y-axis-label-left')
      .attr('transform', 'rotate(-90)')
      .attr('y', -margin.left + 20)
      .attr('x', -(height / 2))
      .style('text-anchor', 'middle')
      .text('Left Y-Axis Label');  // Change this to match your data

    svg.append('text')
      .attr('class', 'y-axis-label-right')
      .attr('transform', 'rotate(-90)')
      .attr('y', width + margin.right - 20)
      .attr('x', -(height / 2))
      .style('text-anchor', 'middle')
      .text('Right Y-Axis Label');  // Change this to match your data*/
  }

  /*draw(): void {
    if (this.state.dataSource.length === 0) {
      return;
    }

    // find the max range of y-axis
    const yMax = this.state.chartData.reduce((acc, group) => {
      const max = group.values.reduce((init, subgroup) => init > subgroup.value ? init : subgroup.value, 0);
      return acc > max ? acc : max;
    }, 0);
    const yMin = this.state.chartData.reduce((acc, group) => {
      const min = group.values.reduce((init, subgroup) => init < subgroup.value ? init : subgroup.value, 0);
      return acc < min ? acc : min;
    }, 0);

    const nextLargerNumber = FindNextLargeNumber(yMax);
    const nextSmallerNumber = FindNextLargeNumber(yMin);

    // set the dimensions and margins of the graph
    const margin = {top: 20, right: 30, bottom: 120, left: 150},
      width = 1000 - margin.left - margin.right,
      height = 500 - margin.top - margin.bottom;

    // append the svg object to the body of the page
    const svg = select(`.${this.props.component}`)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

    // List of x-axis
    const xAxis: string[] = this.state.dataSource[0].children[0].values.map((d) => d.label);

    // Add X axis
    const x: any = scalePoint()
      .domain(xAxis)
      .range([0, width]);

    svg.append('g')
      .attr('transform', 'translate(0,' + height + ')')
      .call(axisBottom(x))
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('transform', 'rotate(-45)');

    // Add Y axis
    const y = scaleLinear()
      .domain([nextSmallerNumber, nextLargerNumber])
      .range([height, 0]);
    /!*svg.append('g')
      .call(axisLeft(y));*!/

    /!*Add a darker line on y-axis 0 value*!/
    const yZero = y(0);
    svg.append('line')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', yZero)
      .attr('y2', yZero)
      .style('stroke', '#505D69');

    // Add the lines
    const valueLine: any = line()
      .x((d: any) => x(d.label))
      .y((d: any) => y(d.value));

    // const formatValue = format('~s');

    // Add y-axis grid lines
    svg
      .append('g')
      .attr('class', 'grid')
      .call(axisLeft(y)
        .tickSize(-width)
        .tickFormat((d: any) => NumberFormatter(d))
        .tickSizeOuter(0));

    // Add the valueLine path
    this.state.chartData.forEach((d) => {
      svg
        .append('path')
        .datum(d.values)
        .attr('stroke', d.color)
        .attr('stroke-dasharray', StrokeDasharrayValue(d.group))
        .attr('stroke-width', 3)
        .style('fill', 'none')
        .attr('d', valueLine);
    });

    // create a tooltip
    const tooltip = select(`.${this.props.component}`)
      .append('div')
      .style('opacity', 0)
      .attr('class', 'tooltip');

    // Three function that change the tooltip when user hover / move / leave a cell
    const mouseover = () => {
      tooltip
        .style('opacity', 1);
    };
    const mousemove = (event, d) => {
      tooltip
        .html(d.measure + '<br/>' + 'Value: ' + NumberWithCommas(d.value))
        .style('left', `${event.layerX + 10}px`)
        .style('top', `${event.layerY}px`);
    };
    const mouseleave = () => {
      tooltip
        .style('opacity', 0);
    };

    // Add the points
    this.state.chartData.forEach((d) => {
      svg
        .append('g')
        .selectAll('dot')
        .data(d.values)
        .join('circle')
        .attr('class', 'myCircle')
        .attr('cx', (d: any) => x(d.label))
        .attr('cy', (d: any) => y(d.value))
        .attr('r', 5)
        // .attr('stroke', d.color)
        // .attr('stroke-width', 3)
        .attr('fill', d.color)
        .on('mouseover', mouseover)
        .on('mousemove', mousemove)
        .on('mouseleave', mouseleave);
    });

    // Add x-axis, y-axis labels
    svg.append('text')
      .attr('class', 'x-axis-label')
      .attr('x', width / 2)
      .attr('y', height + 80)
      .text('Period');

    /!*!// Add the points
    svg
      .selectAll('dot')
      .data(this.state.chartData)
      .enter()
      .append('g')
      .style('fill', (d) => color(d.name))
      .selectAll('point')
      .data((d) => d.values)
      .enter()
      .append('circle')
      .attr('cx', (d) => x(d.label))
      .attr('cy', (d) => y(d.value))
      .attr('r', 5)
      .attr('stroke', 'white');*!/

    /!*!// Add the valueLine path.
    this.state.dataSource.forEach((group) => {
      /!*svg.append('path')
        .datum(group.values)
        .attr('stroke', group.color)
        .attr('stroke-width', 3)
        .style('fill', 'none')
        .attr('d', valueLine);*!/
    });*!/

    // Add the points
    /!*this.state.dataSource.forEach((group) => {
      svg
        .selectAll('myDots')
        .data(group.values)
        .enter()
        .append('g')
        .style('fill', group.color)
        .selectAll('myPoints')
        .data(group.values)
        .enter()
        .append('circle')
        .attr('cx', (d: any) => x(d.label))
        .attr('cy', (d) => y(d.value))
        .attr('r', 5)
        .attr('stroke', 'white')
    });*!/

    /!* const axisLabelX = -30;
     const axisLabelY = height / 2;

     // set the ranges
     const x: any = scaleBand().range([0, width]);
     const y: any = scaleLinear().range([height, 0]);

     const correctXScale = (d) => {
       return x(d.name) + width / this.state.dataSource[0].values.length / 2;
     };

     const valueLine: any = line()
       .x((d: any) => correctXScale(d))
       .y((d: any) => y(d.value));

     const svg = select(`.${this.props.component}`).append('svg')
       .attr('width', width + margin.left + margin.right)
       .attr('height', height + margin.top + margin.bottom)
       .append('g')
       .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

     this.state.dataSource[0].values.forEach((d) => {
       d.value = +d.value;
     });

     x.domain(this.state.dataSource[0].values.map((d) => d.label));

     y.domain([0, max(this.state.dataSource[0].values, (d: any) => d.value)]);

     // Add the valueLine path.
     this.state.dataSource.forEach((obj) => {
       svg.append('path')
         .datum(obj.values)
         .attr('stroke', obj.color)
         .attr('stroke-width', 3)
         .style('fill', 'none')
         .attr('d', valueLine);
     });

     svg.append('g')
       .attr('class', 'grid')
       .call(axisLeft(y)
         .ticks(5)
         .tickSize(-width)
         .tickSizeOuter(0));

     /!*svg.append('g')
       .attr('class', 'grid')
       .attr('transform', 'translate(' + width + ',' + 0 + ')')
       .call(axisRight(y)
         .ticks(5)
         .tickSize(0)
         .tickSizeOuter(0));*!/

     svg.append('g')
       .attr('class', 'grid')
       .attr('transform', 'translate(0,' + height + ')')
       .call(axisBottom(x)
         .ticks(0)
         .tickSize(0));

     svg.append('g')
       .attr('transform', 'translate(' + axisLabelX + ', ' + axisLabelY + ')')
       .append('text')
       .attr('text-anchor', 'middle')
       .attr('transform', 'rotate(-90)')
       .attr('y', -5)
       .attr('color', '#999999')
       .attr('font-size', '11px')
       .attr('font-family', 'Poppins-Medium')
       .text(this.props.group);

     svg.append('text')
       .attr('class', 'x label')
       .attr('text-anchor', 'middle')
       .attr('color', '#999999')
       .attr('font-size', '11px')
       .attr('font-family', 'Poppins-Medium')
       .attr('x', width / 2)
       .attr('y', height + 40)
       .text(this.props.period);*!/
  }*/

  render(): React.ReactNode {
    return (
      <div
        className={`${this.props.component}`}
        style={{ overflowX: 'scroll' }}
      />
    );
  }

}
